'use client';

import Link from 'next/link';
import { LandingHeader } from '@/components/landing/header';
import { LandingBreadcrumbs } from '@/components/landing/breadcrumbs';
import { PROJECT_SETTINGS_EXAMPLE } from '@/lib/project-settings';

const exampleJson = JSON.stringify(PROJECT_SETTINGS_EXAMPLE, null, 2);

const fields = [
  {
    path: 'configVersion',
    type: 'string',
    required: true,
    meaning: 'Version of this settings file format. Use "1.0" for the current format.',
    example: '"1.0"',
  },
  {
    path: 'project',
    type: 'object',
    required: true,
    meaning: 'Identifies the project and its main LaTeX file.',
    example: '—',
  },
  {
    path: 'project.type',
    type: 'string',
    required: true,
    meaning: 'Kind of project. LaTeX projects use "latex".',
    example: '"latex"',
  },
  {
    path: 'project.root',
    type: 'string',
    required: true,
    meaning: 'Project root folder. "." means the project root itself.',
    example: '"."',
  },
  {
    path: 'project.mainTexPath',
    type: 'string',
    required: true,
    meaning: 'Path to the main .tex file, relative to project.root. This is the file used as the compile entry point.',
    example: '"hello-world/main.tex"',
  },
  {
    path: 'latex',
    type: 'object',
    required: true,
    meaning: 'LaTeX toolchain settings for this project.',
    example: '—',
  },
  {
    path: 'latex.compiler',
    type: 'object',
    required: true,
    meaning: 'The compiler used to build the PDF.',
    example: '—',
  },
  {
    path: 'latex.compiler.name',
    type: 'string',
    required: true,
    meaning: 'Compiler name. Current projects typically use "tectonic". Other names are allowed.',
    example: '"tectonic"',
  },
  {
    path: 'latex.compiler.version',
    type: 'string',
    required: true,
    meaning: 'Compiler version as text (not a number). For example "0.15.0".',
    example: '"0.15.0"',
  },
  {
    path: 'sync',
    type: 'object',
    required: true,
    meaning: 'Editor sync defaults stored with the project.',
    example: '—',
  },
  {
    path: 'sync.autoCompile',
    type: 'boolean',
    required: true,
    meaning: 'When true, the project should recompile after you edit. Use true or false, not "true".',
    example: 'true',
  },
  {
    path: 'sync.autosave',
    type: 'boolean',
    required: true,
    meaning: 'When true, the project should save automatically. The property name is autosave (lowercase s).',
    example: 'true',
  },
];

export default function ProjectSettingJsonSchemaHelpPage() {
  return (
    <>
      <LandingHeader />
      <main>
        <section className="bg-background py-12 md:py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <LandingBreadcrumbs
              items={[
                { label: 'Help', href: '/help' },
                { label: 'Project Settings JSON Schema' },
              ]}
            />

            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Project Settings JSON Schema
            </h1>
            <p className="text-lg text-muted-foreground mb-10">
              Every LaTeX project includes a settings file. This page explains
              each field, the allowed types, and how to edit the file without
              breaking your project.
            </p>

            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 rounded-lg p-6 mb-10">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Where is this file?
              </h2>
              <p className="text-muted-foreground mb-3">
                In the file explorer, open:
              </p>
              <code className="block rounded-md bg-white dark:bg-black/20 border border-blue-100 dark:border-blue-800/40 px-3 py-2 text-sm font-mono text-foreground">
                /project/project.rudra.config.json
              </code>
              <p className="text-muted-foreground mt-3">
                While that file is open in the editor, hover a property for a
                hint, use autocomplete for valid keys, and watch for red
                underlines if a value does not match the schema.
              </p>
            </div>

            <h2 className="text-2xl font-bold mb-3">What this file controls</h2>
            <p className="text-muted-foreground mb-8">
              <code className="text-sm bg-secondary px-1.5 py-0.5 rounded">project.rudra.config.json</code>{' '}
              stores project-level settings: which <code className="text-sm">.tex</code> file is
              the main file, which compiler to use, and whether auto-compile and
              autosave are on. Extra properties are not allowed. If you add a
              key that is not listed below, the editor will flag it as invalid.
            </p>

            <h2 className="text-2xl font-bold mb-4">Field reference</h2>
            <div className="overflow-x-auto border border-border rounded-lg mb-10">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Property</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">Required</th>
                    <th className="px-4 py-3 font-semibold">What it means</th>
                    <th className="px-4 py-3 font-semibold">Example</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field) => (
                    <tr key={field.path} className="border-t border-border align-top">
                      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                        {field.path}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{field.type}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {field.required ? 'Yes' : 'No'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{field.meaning}</td>
                      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                        {field.example}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold mb-3">Valid example</h2>
            <p className="text-muted-foreground mb-4">
              A complete settings file looks like this:
            </p>
            <pre className="bg-gray-900 text-gray-100 rounded-lg p-6 overflow-x-auto mb-10 text-sm font-mono">
              {exampleJson}
            </pre>

            <h2 className="text-2xl font-bold mb-3">Common mistakes</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-10">
              <li>
                Using <code className="text-sm bg-secondary px-1.5 py-0.5 rounded">&quot;true&quot;</code> or{' '}
                <code className="text-sm bg-secondary px-1.5 py-0.5 rounded">&quot;false&quot;</code> for{' '}
                <code className="text-sm">sync.autoCompile</code> or{' '}
                <code className="text-sm">sync.autosave</code>. These must be booleans:{' '}
                <code className="text-sm">true</code> / <code className="text-sm">false</code>.
              </li>
              <li>
                Setting <code className="text-sm">latex.compiler.version</code> to a number such as{' '}
                <code className="text-sm">0.15</code>. It must be a string, for example{' '}
                <code className="text-sm">&quot;0.15.0&quot;</code>.
              </li>
              <li>
                Removing <code className="text-sm">project.mainTexPath</code>. The main TeX path is required.
              </li>
              <li>
                Adding extra keys (for example at the top level or inside{' '}
                <code className="text-sm">sync</code>). Unknown properties are rejected.
              </li>
              <li>
                Changing <code className="text-sm">configVersion</code> away from{' '}
                <code className="text-sm">&quot;1.0&quot;</code> unless a newer format has been published.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-3">Hints in the editor</h2>
            <p className="text-muted-foreground mb-6">
              When you edit <code className="text-sm">/project/project.rudra.config.json</code>,
              the editor uses this schema automatically:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-10">
              <li>Hover a property name to read what it does.</li>
              <li>Start typing inside an object to see allowed property names.</li>
              <li>Invalid types, missing required fields, and unknown keys are underlined.</li>
            </ul>

            <div className="border-t border-border pt-8">
              <p className="text-muted-foreground mb-4">
                Still stuck? Visit{' '}
                <Link href="/help" className="text-primary hover:underline">
                  Help
                </Link>{' '}
                or{' '}
                <Link href="/contact-us" className="text-primary hover:underline">
                  contact support
                </Link>
                .
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Last updated:</strong> Aug 17, 2026
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
